<template>
  <div class="md-layout">
    <div class="sensor-header">
  <!-- Left side: your buttons -->
  <div class="time-selector">

    <!-- Tabs Navigation -->
    <div class="tabs-toggle-container md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <div class="tab-buttons" ref="tabsContainer">
        <md-button
          v-for="range in tabs"
          :key="range"
          class="md-raised"
          :class="activeTab === range ? 'md-success' : 'md-default'"
          @click="setActiveTab(range, $event)"
        >
          {{ range }}
        </md-button>

        <!-- Inline Timestamp next to tabs -->
        <div class="timestamp-inline">
          Časový rozsah: {{ formatTimestamp(startTimestamp) }} - {{ formatTimestamp(endTimestamp) }}
        </div>

        <!-- Custom Range Calendar Popup -->
        <div
          v-if="showCustomCalendar"
          class="custom-calendar-popup"
          :style="calendarPosition"
          ref="calendarPopup"
        >
          <div class="calendar-header">
              <button class="month-nav" @click="prevMonth">&lt;</button>
               <span class="month-name">
                {{ currentMonth.toLocaleString('sk-SK', { month: 'long', year: 'numeric' }) }}
               </span>
              <button class="month-nav" @click="nextMonth">&gt;</button>
          </div>
          <div class="calendar-grid">
            <div class="day-name" v-for="d in dayNames" :key="d">{{ d }}</div>
            <div
              class="calendar-day"
              v-for="day in daysInMonth"
              :key="day.date.toDateString()"
              :class="{
                selected: isSelected(day.date),
                inRange: isInRange(day.date),
                today: isToday(day.date)
              }"
              @click="selectDay(day.date)"
            >
              {{ day.date.getDate() }}
            </div>
          </div>
        </div>
      </div>
    </div>
   <!-- your tabbed menu ends here -->
  </div>
  
  <!-- Right side: sensor description -->
<div class="sensor-description">
  <md-card class="md-card-info">
    <md-card-content>
      <p style="margin: 0;"><b>Senzor: </b>{{ currentSensor?.description || "No description available" }}</p>
    </md-card-content>
  </md-card>
</div>

</div> <!-- end of  sensor header -->

       <!-- Stats Card -->
       <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25" v-if="!sensorOffline">
      <stats-card header-color="blue">
        <template #header>
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <p class="category">Merač spotreby {{ sensorID }}</p>
          <h3 class="title">
            <animated-number :value="temperatureStats.min" :duration="1000" /> kWh
          </h3>
        </template>
        <template slot="footer">
          Minimálna hodnota v rozsahu dát
        </template>
      </stats-card>
    </div>
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25" v-if="!sensorOffline">
      <stats-card header-color="blue">
        <template #header>
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <p class="category">Merač spotreby {{ sensorID }}</p>
          <h3 class="title">
            <animated-number :value="temperatureStats.avg" :duration="1000" /> kWh
          </h3>
        </template>
        <template slot="footer">
          Priemerná hodnota v rozsahu dát
        </template>
      </stats-card>
    </div>
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25" v-if="!sensorOffline">
      <stats-card header-color="blue">
        <template #header>
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <p class="category">Merač spotreby {{ sensorID }}</p>
          <h3 class="title">
            <animated-number :value="temperatureStats.max" :duration="1000" /> kWh
          </h3>
        </template>
        <template slot="footer">
          Maximálna prijatá hodnota v rozsahu dát
        </template>
      </stats-card>
    </div>
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25" v-if="!sensorOffline">
      <stats-card header-color="blue">
        <template #header>
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <p class="category">Merač spotreby {{ sensorID }}</p>
          <h3 class="title">
            <animated-number :value="temperatureStats.last" :duration="1000" /> kWh
          </h3>
        </template>
        <template slot="footer">
          Posledná prijatá hodnota
        </template>
      </stats-card>
    </div>

    <!-- Chart -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100" v-if="!sensorOffline">
      <md-card header-animation="false" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <div class="card-icon"><md-icon>insert_chart</md-icon></div>
          <h4 class="title">Priemerná spotreba v rozsahu {{ formatTimestamp(startTimestamp) }} - {{ formatTimestamp(endTimestamp) }}</h4>
        </md-card-header>
        <md-card-content style="height: 100%; padding: 1rem">
          <simple-bar-chart
            v-if="chartData.labels.length"
            :chart-data="chartData"
            :chart-options="chartOptions"
          />
          <div v-else class="no-data">No data for selected range</div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Sensor Offline -->
    <div v-else class="md-layout-item md-size-100">
      <md-card class="md-card-warning" style="padding: 2rem; text-align: center">
        <md-icon style="font-size: 48px; color: red">warning</md-icon>
        <h3>Dáta senzoru {{ sensorID }}</h3>
        <p>Pre zvolený senzor a časový rozsah nie sú k dispozícii žiadne dáta.</p>
      </md-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SimpleBarChart from "@/pages/Dashboard/SimpleBarChart.vue";
import { StatsCard, AnimatedNumber } from "@/components";
import { sensors } from "@/utils/userInfo.js";

export default {
  name: "TemperatureRangeChart",
  components: { StatsCard, AnimatedNumber, SimpleBarChart },
  data() {
    return {
      tabs: ["Dnes", "Posledných 7 dní", "Posledných 30 dní", "Tento rok", "Vlastný rozsah"],
      activeTab: "Dnes",
      startTimestamp: 0,
      endTimestamp: 0,
      showCustomCalendar: false,
      calendarPosition: { top: "0px", left: "0px" },
      currentMonth: new Date(),
      dayNames: ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"],
      selectedStart: null,
      selectedEnd: null,

      sensorOffline: false,
      temperatureStats: { min: 0, max: 0, avg: 0, last: 0 },
      chartData: { labels: [], datasets: [] },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true } },
        plugins: { legend: { display: true } },
      },
    };
  },
  computed: {
    sensorID() {
      return this.$route.path.split("/").pop();
    },
    currentSensor() {
    const id = this.$route.path.split("/").pop();
    return sensors.find(s => s.id === id);
  },
    daysInMonth() {
      const days = [];
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      for (let d = firstDay; d <= lastDay; d.setDate(d.getDate() + 1)) {
        days.push({ date: new Date(d) });
      }
      return days;
    },
  },
  mounted() {
    this.calculateTimestamps();
    document.addEventListener("click", this.handleClickOutside);
    this.updateData();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    startTimestamp: "updateData",
    endTimestamp: "updateData",
    "$route.path": "updateData",
  },
  methods: {
    prevMonth() {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
  },
  nextMonth() {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
  },
    async updateData() {
      try {
        const deviceID = this.sensorID;
        const response = await axios.post("https://devel.ceelabs.com/energiareal/DataDiff.php", {
          start: this.startTimestamp,
          end: this.endTimestamp,
          deviceID,
        });

        const timeSeries = response.data.data || {};

        if (!Object.keys(timeSeries).length) {
          this.sensorOffline = true;
          this.chartData = { labels: [], datasets: [] };
          this.temperatureStats = { min: 0, max: 0, avg: 0, last: 0 };
          return;
        }

        this.sensorOffline = false;
        const rangeDuration = this.endTimestamp - this.startTimestamp;
        const isMultiDay = rangeDuration > 86400;

        let labels = [], values = [];

        if (!isMultiDay) {
          // SINGLE DAY: keep all points
          labels = Object.keys(timeSeries).map(ts => {
            const d = new Date(ts * 1000);
            return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
          });
          values = Object.values(timeSeries).map(Number);
        } else {
          // MULTI-DAY: aggregate per day
          const dailySums = {}, dailyCounts = {};
          for (const [tsStr, val] of Object.entries(timeSeries)) {
            const ts = parseInt(tsStr);
            const d = new Date(ts * 1000);
            const dayKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            dailySums[dayKey] = (dailySums[dayKey] || 0) + val;
            dailyCounts[dayKey] = (dailyCounts[dayKey] || 0) + 1;
          }
          Object.keys(dailySums).sort().forEach(dayKey => {
            const [y,m,d] = dayKey.split('-');
            labels.push(`${d}.${m}.${y}`);
            values.push(dailySums[dayKey]/dailyCounts[dayKey]);
          });
        }

        // Update chart
        this.chartData = {
          labels,
          datasets: [
            {
              label: isMultiDay ? "Denná priemerná spotreba energie (kWh)" : "Spotreba energie v kWh",
              backgroundColor: "rgba(9,178,199,255)",
              data: values.map(Number),
            }
          ]
        };

        // Recalculate stats from chart
        this.recalcStatsFromChart();
      } catch (err) {
        this.sensorOffline = true;
        this.temperatureStats = { min: 0, max: 0, avg: 0, last: 0 };
      }
    },

    recalcStatsFromChart() {
      if (!this.chartData.datasets.length || !this.chartData.datasets[0].data.length) {
        this.temperatureStats = { min: 0, max: 0, avg: 0, last: 0 };
        return;
      }
      const values = this.chartData.datasets[0].data.map(Number);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = values.reduce((a,b)=>a+b,0)/values.length;
      const last = values[values.length-1];
      this.temperatureStats = { min, max, avg, last };
    },

    setActiveTab(tab,event) {
      this.activeTab = tab;
      if (tab !== "Vlastný rozsah") {
        this.showCustomCalendar = false;
        this.calculateTimestamps();
      } else {
        this.showCustomCalendar = true;
        this.selectedStart = null;
        this.selectedEnd = null;
        this.$nextTick(()=>{
          const btn = event.currentTarget;
          this.calendarPosition = {
            top: btn.offsetTop + btn.offsetHeight + "px",
            left: btn.offsetLeft + "px"
          };
        });
      }
    },

    calculateTimestamps() {
      const now = new Date();
      let start;
      switch(this.activeTab) {
        case "Dnes": start=new Date(now.setHours(0,0,0,0)); break;
        case "Posledných 7 dní": start=new Date(now); start.setDate(start.getDate()-7); break;
        case "Posledných 30 dní": start=new Date(now); start.setDate(start.getDate()-30); break;
        case "Tento rok": start=new Date(now.getFullYear(),0,1); break;
      }
      this.startTimestamp = Math.floor(start.getTime()/1000);
      this.endTimestamp = Math.floor(Date.now()/1000);
    },

    formatTimestamp(ts) {
      const d = new Date(ts*1000);
      return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} (${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')})`;
    },

    selectDay(date) {
      if(!this.selectedStart || (this.selectedStart && this.selectedEnd)){
        this.selectedStart = date; this.selectedEnd = null;
      } else if(date >= this.selectedStart){
        this.selectedEnd = date; this.finalizeCustomRange();
      } else { this.selectedStart = date; }
    },
    finalizeCustomRange() {
      if(!this.selectedStart || !this.selectedEnd) return;
      const start = new Date(this.selectedStart); start.setHours(0,0,0,0);
      const end = new Date(this.selectedEnd); end.setHours(23,59,59,999);
      this.startTimestamp = Math.floor(start.getTime()/1000);
      this.endTimestamp = Math.floor(end.getTime()/1000);
      this.showCustomCalendar = false;
    },
    isSelected(date){ return (this.selectedStart && date.toDateString() === this.selectedStart.toDateString()) || (this.selectedEnd && date.toDateString() === this.selectedEnd.toDateString()); },
    isInRange(date){ return this.selectedStart && this.selectedEnd && date>this.selectedStart && date<this.selectedEnd; },
    isToday(date){ return date.toDateString() === new Date().toDateString(); },
    handleClickOutside(event){
      if(this.showCustomCalendar && !this.$refs.calendarPopup.contains(event.target) && !this.$refs.tabsContainer.contains(event.target)){
        this.showCustomCalendar = false;
      }
    }
  }
};
</script>

<style scoped>
.sensor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  width: 99%;
}

.sensor-description {
  flex: 1;                /* take all remaining space */
  margin-left: 1rem;      /* space between buttons and card */
}

.sensor-description .md-card {
  width: 100%;            /* make the card fill its flex container */
  background-color: #09b1c777;   /* soft green */
  border-left: 4px solid #09b2c7; /* optional accent */
  font-style: italic;
  color: #000000;
}
.tab-buttons { display:flex; gap:8px; position:relative; align-items:center; }
.timestamp-inline { font-size:14px; color:#333; margin-left:12px; white-space:nowrap; }
.custom-calendar-popup { position:absolute; background:white; border:1px solid #ccc; padding:10px; border-radius:6px; z-index:1000; box-shadow:0 2px 10px rgba(0,0,0,0.2); }
.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between; /* arrows on edges */
  font-weight: bold;
  margin-bottom: 6px;
  text-align: center;
}

.month-nav {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
}

.month-name {
  flex: 1;              /* take remaining space */
  text-align: center;    /* center text between arrows */
  white-space: nowrap;   /* prevent line breaks */
}
.calendar-grid { display:grid; grid-template-columns:repeat(7,30px); gap:2px; }
.day-name { font-weight:bold; text-align:center; font-size:12px; }
.calendar-day { width:30px; height:30px; text-align:center; line-height:30px; cursor:pointer; border-radius:4px; }
.calendar-day.selected { background:#4caf50; color:white; }
.calendar-day.inRange { background:#a5d6a7; }
.calendar-day.today { border:1px solid #4caf50; }
.no-data { text-align:center; color:#777; font-style:italic; margin-top:20px; }
</style>
